import React, { useState } from 'react';
import { hot } from 'react-hot-loader';
import Link from 'redux-first-router-link';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import Slider from '../../hocs/Slider/Slider';
import BackgroundMedia from './BackgroundMedia/BackgroundMedia';
import Button from './Button/Button';
import Dropbox from './Dropbox/Dropbox';
import Embed from './Embed/Embed';
import Heading from './Heading/Heading';
import Image from './Image/Image';
import InlineCategoryContent from './InlineCategoryContent/InlineCategoryContent';
import List from './List/List';
import Paragraph from './Paragraph/Paragraph';
import Placeholder from './Placeholder/Placeholder';
import Products from './Products/Products';
import Separator from './Separator/Separator';
import Spacer from './Spacer/Spacer';
import SectionTitle from './SectionTitle/SectionTitle';
import Stockists from './Stockists/Stockists';
import Table from './Table/Table';
import Video from './Video/Video';
import VideoEmbed from './VideoEmbed/VideoEmbed';
import Zoho from './Zoho/Zoho';
import { percentToColumn } from '../../utils/columns';
import { purifier } from '../../utils/dom';
import { translate } from '../../utils/locale';
import { deepMerge, hasAttrInObj } from '../../utils/object';
import { titleCase, urlify, camelCase, cleanInternalLinks } from '../../utils/string';
import { isRestricted } from '../../utils/restrict';
import s from './TemplateParser.scss';

const components = {
	acfInlineCategoryContent: InlineCategoryContent,
	acfDropboxFolder: Dropbox,
	acfPlaceholder: Placeholder,
	acfProducts: Products,
	acfStockists: Stockists,
	acfZoho: Zoho,
	coreButton: Button,
	coreEmbed: VideoEmbed,
	coreHeading: Heading,
	coreHtml: Embed,
	coreImage: Image,
	coreParagraph: Paragraph,
	coreSeparator: Separator,
	coreSpacer: Spacer,
	coreVideo: Video,
	coreTable: Table,
};

const containers = {};

const heightSettings = ({ attrs = {}, setup }) => {
	const extraProps = {};
	const settings = {};

	if ((attrs.fixedHeightValue && setup.breakPoint === 'desktop')
		|| (attrs.fixedHeightValueMd && setup.breakPoint === 'tablet')
		|| (attrs.fixedHeightValueSm && setup.breakPoint === 'mobile')
	) {
		extraProps.className = ['u_fillContainer'];
		extraProps.style = {
			overflow: 'hidden',
		};
		settings.fill = true;
		settings.focalPoint = attrs.focalPoint;
	}

	if (attrs.fixedHeightValue && setup.breakPoint === 'desktop') extraProps.style.height = `${attrs.fixedHeightValue}vh`;
	if (attrs.fixedHeightValueMd && setup.breakPoint === 'tablet') extraProps.style.height = `${attrs.fixedHeightValueMd}vh`;
	if (attrs.fixedHeightValueSm && setup.breakPoint === 'mobile') extraProps.style.height = `${attrs.fixedHeightValueSm}vh`;

	return [extraProps, settings];
};

const generalSettings = ({ customProps, attrs = {}, template }) => {
	const attrsClassName = attrs.className || [];

	const generalProps = {
		className: [
			s.container,
			s[template],
			...attrsClassName.map((cN) => s[`${template}${cN}`]),
		],
		style: {},
	};

	// Colors
	if (attrs.backgroundColor) generalProps.style.backgroundColor = attrs.backgroundColor;
	if (attrs.textColor) {
		generalProps.style.color = attrs.textColor;
		generalProps.className.push('u_white');
	}

	// Alignment
	if (attrs.align && attrs.align !== 'full') generalProps.style.textAlign = attrs.align;
	if (attrs?.layout?.justifyContent && attrs.layout.justifyContent !== 'full') generalProps.style.justifyContent = attrs.layout.justifyContent;
	if (attrs.verticalAlignment) generalProps.className[generalProps.className.length] = s[`container__v${titleCase(attrs.verticalAlignment)}`];
	if (attrs.align === 'full') generalProps.className[generalProps.className.length] = s.container__alignFull;

	const extraProps = deepMerge(generalProps, customProps);
	extraProps.className = extraProps.className.join(' ');

	return extraProps;
};

const getElement = (attrs, setup) => {
	const extraProps = {};
	let Element = 'div';

	if (attrs.href) {
		const link = cleanInternalLinks(translate(setup, attrs.href), setup);
		const externalLink = /^(https?|mailto|tel|#)/.test(link);

		if (attrs.target || externalLink) {
			Element = 'a';
			extraProps.href = link;
			extraProps.target = '_blank';
			extraProps.rel = 'noopener noreferrer';
		} else {
			Element = Link;
			extraProps.to = link.replace(/\/+$/, '');
		}
	}

	return [Element, extraProps];
};

const getTemplate = (template, attrs, setup, useComponents = true) => {
	if (isRestricted(setup, attrs)) return false;
	if (components[template]) {
		if (useComponents) return components[template];
		return NoContainer;
	}
	if (containers[template]) return containers[template];

	if (['ccBlocksMeta', 'acfSectionTitle'].indexOf(template) === -1) console.log('TEMPLATE MISSING: ', template);
	return null;
};

containers.acfCategory = ({ content = [], setup }) => {
	if (!Array.isArray(content)) content = [{ content: { inlineMedia: content.inlineMedia }, key: content.key }];
	return (
		<>
			{
				content.map((childSection) => {
					let tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					if (tpl === 'content') tpl = 'acfInlineCategoryContent';
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) {
						if (ChildElement === null) console.log('TEMPLATE MISSING FROM ACF CATEGORY: ', nextContent);
						return null;
					}
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} />;
				})
			}
		</>
	);
};

containers.ccBlocksAccordion = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) {
						if (ChildElement === null) console.log('TEMPLATE MISSING FROM ACCORDION: ', nextContent);
						return null;
					}
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.ccBlocksAccordionItem = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};
	const accordionKey = urlify(attrs.title.slice(0, 15));

	const [accordianToggled, toggleAccordian] = useState(props.hashLocation === accordionKey);
	const toggleAccordianHandler = (e) => {
		e.preventDefault();
		toggleAccordian((prevState) => !prevState);
	};

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			<button aria-controls={accordionKey} aria-expanded={accordianToggled} type="button" onClick={toggleAccordianHandler} className={[s.ccBlocksAccordionItem_title, (accordianToggled ? s.ccBlocksAccordionItem_title__expanded : null)].join(' ')}>
				<span>{ attrs.title }</span>
			</button>
			<div aria-hidden={!accordianToggled} id={accordionKey} className={[s.ccBlocksAccordionItem_content, (accordianToggled ? s.ccBlocksAccordionItem_content__expanded : null)].join(' ')}>
				{
					content.map((childSection) => {
						const tpl = Object.keys(childSection)[0];
						const nextContent = childSection[tpl];
						const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
						if (!ChildElement) {
							if (ChildElement === null) console.log('TEMPLATE MISSING FROM ACCORDION ITEM: ', nextContent);
							return null;
						}
						return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
					})
				}
			</div>
		</div>
	);
};

containers.ccBlocksPuff = ({ attrs = {}, content = [], uid, setup, template, ...props }) => {
	const [customProps, settings] = heightSettings({ attrs, setup });

	const [Element, elementProps] = getElement(attrs, setup);
	customProps.className = [s[`container__${camelCase(attrs.layout || 'center Center')}`]];
	const extraProps = generalSettings({ customProps: deepMerge(customProps, elementProps), attrs, template });

	return (
		<Element {...extraProps}>
			<BackgroundMedia content={attrs} uid={uid} {...settings} {...props} />
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) {
						if (ChildElement === null) console.log('TEMPLATE MISSING FROM PUFF: ', nextContent);
						return null;
					}
					return <ChildElement template={tpl} key={nextContent.key} isPuffContent setup={setup} {...nextContent} {...props} />;
				})
			}
		</Element>
	);
};
containers.ccBlocksPuffAlt = containers.ccBlocksPuff;

const scrollRight = (e) => {
	e.preventDefault();
	e.currentTarget.nextSibling.scrollLeft += (window.innerWidth * 0.7) + 5;
};

const SliderWrapper = ({ attrs = {}, sliderAttrs, children }) => {
	if (attrs?.freeFlow && attrs?.align === 'full') {
		return (
			<div className={s.container__ccBlocksSlider_inner}>
				<button className={s.container__ccBlocksSlider_scroll} onClick={scrollRight} type="button" aria-label="Scroll Right">
					<svg width="7" height="10" viewBox="0 0 7 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L6 5L1 9" stroke="black" strokeWidth="0.3" /></svg>
					<svg width="12" height="18" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L11 9L1 17" stroke="black" strokeWidth="0.3" /></svg>
				</button>
				<div className={s.container__ccBlocksSlider_inner_overflow}>
					{ children }
				</div>
			</div>
		);
	}
	return (
		<Slider {...sliderAttrs}>
			{ children }
		</Slider>
	);
};

containers.ccBlocksSlider = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const [customProps] = heightSettings({ attrs, setup });
	let adaption = false;

	const [widthSubColumn, totalSubColumns] = content.filter((section) => Object.keys(section)[0] === 'coreColumns').reduce((acc, next) => {
		const col = next.coreColumns.content.length;
		acc[1] += col;
		if (col > acc[0]) acc[0] = col;
		return acc;
	}, [0, 0]) || false;
	if (widthSubColumn) {
		adaption = 'columns';
		customProps.className = [s[`slider__${widthSubColumn}`]];
	}

	const sliderHasLinks = hasAttrInObj(content, 'href');
	if (sliderHasLinks) {
		if (adaption) adaption = [adaption, 'links'];
		else adaption = 'links';
	}

	const sliderAttrs = {
		transitionType: attrs?.transitionType || 'fade',
		transitionSpeed: attrs?.transitionSpeed || 300,
		infinite: typeof attrs.infinite !== 'undefined' ? attrs.infinite : 1,
		autoplay: attrs?.autoplay !== false && (attrs?.transitionDelay || 3000),
		interactionTypes: sliderHasLinks ? [] : ['touch'],
		startIndex: 0,
		widthSubColumn,
		totalSubColumns,
		adaption,
		breakPoint: setup.breakPoint,
	};

	if (widthSubColumn) {
		sliderAttrs.interactionTypes.push('drag');
		sliderAttrs.interactionTypes.push('touch');
		sliderAttrs.showArrows = false;
	}

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			<SliderWrapper attrs={attrs} sliderAttrs={sliderAttrs}>
				{
					content.map((childSection) => {
						const tpl = Object.keys(childSection)[0];
						const nextContent = childSection[tpl];
						const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
						if (!ChildElement) {
							if (ChildElement === null) console.log('TEMPLATE MISSING FROM SLIDER: ', nextContent);
							return null;
						}
						return <ChildElement template={tpl} key={nextContent.key} setup={setup} slider {...nextContent} {...props} />;
					})
				}
			</SliderWrapper>
		</div>
	);
};

containers.coreButtons = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {
		className: ['u_buttons'],
	};
	if (!content) return null;
	const isManyButtons = content.filter((tpl) => !isRestricted(setup, tpl[Object.keys(tpl)[0]].attrs));

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) {
						if (ChildElement === null) console.log('TEMPLATE MISSING FROM BUTTONS: ', nextContent);
						return null;
					}
					return <ChildElement template={tpl} key={nextContent.key} isManyButtons={isManyButtons.length} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.coreColumn = ({ attrs = {}, content = [], setup, template, column, isFluid, ...props }) => {
	const customProps = {
		className: [],
	};
	const [Element, elementProps] = getElement(attrs, setup);
	if (Element !== 'div') props.wrapperHasLink = 1;

	if (isFluid) {
		customProps.className = ['u_column', 'u_width__fluid', 'u_width__4'];
	} else if (attrs?.width && attrs.width.indexOf('px') !== -1) {
		customProps.className = ['u_column', 'u_width__set'];
		customProps.style = { width: attrs.width };
	} else if (column) customProps.className = ['u_column', `u_width__${column}`];

	if (!attrs?.width) customProps.className.push('u_width__grow');

	const imageOnlyInColumn = content && content.filter((section) => Object.keys(section).join('') !== 'coreImage').length === 0;
	if (imageOnlyInColumn) customProps.className.push(s.container__imageOnly);

	const extraProps = generalSettings({ customProps: deepMerge(customProps, elementProps), attrs, template });

	return (
		<Element {...extraProps}>
			{
				content && content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement || !column) {
						if (!ChildElement) console.log('TEMPLATE MISSING FROM COLUMN: ', nextContent);
						return null;
					}
					return <ChildElement template={tpl} key={nextContent.key} column={column} setup={setup} {...nextContent} {...props} />;
				})
			}
		</Element>
	);
};

// Fluid layout removes the columns container.
const ColumnsWrapper = ({ isFluid = false, extraProps, children }) => {
	if (isFluid) return children;
	return (
		<div {...extraProps}>
			{ children }
		</div>
	);
};

containers.coreColumns = ({ attrs = {}, content = [], setup, template, slider = false, ...props }) => {
	const customProps = {};

	const initialColumnWidths = percentToColumn(content);
	const rowOnMobile = attrs.isStackedOnMobile === 0;
	customProps.className = [(rowOnMobile ? s.coreColumns__rowOnMobile : null)];
	const extraProps = generalSettings({ customProps, attrs, template });
	const isFluid = !slider && setup.breakPoint !== 'desktop' && initialColumnWidths.filter((col) => col !== 4).length === 0;
	if (slider && props['aria-hidden']) extraProps['aria-hidden'] = 'true';

	return (
		<ColumnsWrapper isFluid={isFluid} extraProps={extraProps}>
			{
				content.map((childSection, i) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) {
						if (ChildElement === null) console.log('TEMPLATE MISSING FROM COLUMNS: ', nextContent);
						return null;
					}
					return <ChildElement template={tpl} key={nextContent.key} column={initialColumnWidths[i]} isFluid={isFluid} setup={setup} {...nextContent} {...props} />;
				})
			}
		</ColumnsWrapper>
	);
};

containers.coreListItem = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ setup, customProps, attrs, template });

	return (
		<li {...extraProps}>
			{ attrs?.content?.rawText && <div dangerouslySetInnerHTML={{ __html: purifier(attrs.content.rawText, {}, setup) }} /> }
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</li>
	);
};

const ListWrapper = ({ ordered = false, extraProps, children }) => {
	if (ordered) {
		return (
			<ol {...extraProps}>
				{ children }
			</ol>
		);
	}
	return (
		<ul {...extraProps}>
			{ children }
		</ul>
	);
};

containers.coreList = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	if (!Array.isArray(content)) return <List content={content} />;

	const extraProps = generalSettings({ setup, customProps, attrs, template });

	return (
		<ListWrapper ordered={attrs?.ordered} extraProps={extraProps}>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</ListWrapper>
	);
};

containers.coreGroup = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) {
						if (ChildElement === null) console.log('TEMPLATE MISSING FROM GROUP: ', nextContent);
						return null;
					}
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.coreMore = () => null;

const NoContainerWrapper = ({ inline = false, align = false, template = '', children }) => {
	if (!inline && align !== 'full' && template !== 'acfProducts' && template !== 'acfPlaceholder') {
		return (
			<div className={s.container__textPage}>
				<div className={s.container_inner}>{ children }</div>
			</div>
		);
	}
	return children;
};

const NoContainer = ({ attrs = {}, content = [], template, setup, ...props }) => {
	const Template = getTemplate(template, attrs, setup);
	if (!Template) return null;

	return (
		<ErrorBoundary message={`CMS TemplateSection: ${template}`} debugData={content}>
			<NoContainerWrapper inline={props.inline} align={attrs?.align} template={template}>
				<Template content={content} attrs={attrs} setup={setup} {...props} />
			</NoContainerWrapper>
		</ErrorBoundary>
	);
};

// Variant classes take precedence, then a preceding section title, then an anchor id.
const ChildWrapper = ({ classes = [], anchorLink, sectionTitle, setup, children }) => {
	if (classes.length > 0) return <div className={classes.join(' ')}>{ children }</div>;
	const addProps = {};
	if (anchorLink) addProps.id = anchorLink;
	if (sectionTitle) {
		return (
			<div {...addProps} className={s.sectionTitle}>
				<SectionTitle setup={setup} content={sectionTitle} />
				{ children }
			</div>
		);
	}
	if (anchorLink) return <div {...addProps}>{ children }</div>;
	return children;
};

const TemplateParser = ({ sections, setup, variantClasses = false, skipFirst = false, ...props }) => {
	if (!sections) return null;
	if (!Array.isArray(sections)) sections = [sections];
	const templateContents = [];
	let first = true;
	let classes = [];
	if (variantClasses) {
		classes = Array.isArray(variantClasses) ? variantClasses : [variantClasses];
		classes = [...classes.filter((c) => !!c).map((c) => s[c])];
	}

	sections.forEach((section, i) => {
		const template = Object.keys(section)[0];
		if (section[template]) {
			const ChildElement = getTemplate(template, section[template].attrs, setup, false);
			if (ChildElement) {
				const nextContent = section[template];
				const sectionTitle = i !== 0 && Object.keys(sections[i - 1])[0] === 'acfSectionTitle' ? sections[i - 1] : null;
				templateContents[templateContents.length] = (
					<ChildWrapper key={nextContent.key} classes={classes} anchorLink={nextContent.anchorLink} sectionTitle={sectionTitle} setup={setup}>
						<ChildElement template={template} setup={setup} first={skipFirst ? false : first} last={sections.length - 1 === i} uid={nextContent.key} {...nextContent} {...props} />
					</ChildWrapper>
				);

				// First section that is visible to customer.
				if (template !== 'coreSpacer') first = false;
			}
		} else {
			console.log('TEMPLATE MISSING FROM BASE: ', section);
		}
	});

	return templateContents;
};

export default hot(module)(TemplateParser);
